import { get, isNil } from "lodash";
import { PROCESS_TRIGGER_NAMES } from "utils/constants";
import { evaluateField, readItemField } from "utils/validation-rules";
import {
  evaluateConditions,
  normalizeFieldKey,
  storageKeyOf,
} from "./conditions";
import { readSlotValue, resolveTarget } from "./hydrate";

export const isBlankValue = (value) =>
  isNil(value) ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

const collectionItemsOf = (entity, context, project) => {
  const fromContext = get(context, ["fields", entity]);
  if (Array.isArray(fromContext)) return fromContext;
  const fromProject = get(project, ["fields", entity]);
  if (Array.isArray(fromProject)) return fromProject;
  return null;
};

const readRequiredValue = (
  accessor,
  slot,
  target,
  fieldDefinition,
  context,
  project,
) => {
  const fromContext = get(context, accessor);
  if (!isBlankValue(fromContext)) return fromContext;
  const buffered = get(project, ["fields", accessor]);
  if (buffered !== undefined) return buffered;
  const fromContextSlot = readSlotValue(slot, target, fieldDefinition, context);
  if (fromContextSlot !== undefined) return fromContextSlot;
  return readSlotValue(slot, target, fieldDefinition, project);
};

export const slotAccessor = (slot, lookupFieldDefinition) => {
  const binding = slot.binding;
  if (!binding || binding.kind !== "entity_field") return null;
  return storageKeyOf(
    binding,
    lookupFieldDefinition(binding.entity, binding.field),
  );
};

export const collectMissingRequired = ({
  formDefinition,
  slots,
  context,
  project,
  lookupFieldDefinition,
}) => {
  const fields = get(formDefinition, "fields") || [];
  if (!fields.length) return [];
  const required = new Map();
  for (const field of fields) {
    if (!field.required) continue;
    if (field.visibleWhen && !evaluateConditions(field.visibleWhen, context))
      continue;
    required.set(
      normalizeFieldKey(field.accessor || `fields.${field.key}`),
      field,
    );
  }
  if (!required.size) return [];

  const missing = [];
  for (const slot of slots) {
    if (slot.type !== "input") continue;
    const accessor = slotAccessor(slot, lookupFieldDefinition);
    if (!accessor) continue;
    const formField =
      required.get(normalizeFieldKey(accessor)) ||
      required.get(
        normalizeFieldKey(`${slot.binding.entity}.${slot.binding.field}`),
      );
    if (!formField) continue;
    const collectionItems = collectionItemsOf(
      slot.binding.entity,
      context,
      project,
    );
    if (collectionItems) {
      const fieldName = slot.binding.field;
      const nestedName = formField.accessor
        ? formField.accessor.slice(formField.accessor.indexOf(".") + 1)
        : fieldName;
      const incomplete = collectionItems.some((item) => {
        const fromItem = readItemField(item, fieldName);
        const value =
          fromItem === undefined ? readItemField(item, nestedName) : fromItem;
        return evaluateField(formField, value, context) || isBlankValue(value);
      });
      if (incomplete) missing.push({ slot, accessor });
      continue;
    }
    const fieldDefinition = lookupFieldDefinition(
      slot.binding.entity,
      slot.binding.field,
    );
    const value = readRequiredValue(
      accessor,
      slot,
      resolveTarget(slot, fieldDefinition),
      fieldDefinition,
      context,
      project,
    );
    if (evaluateField(formField, value, context) || isBlankValue(value)) {
      missing.push({ slot, accessor });
    }
  }
  return missing;
};

export const isRequiredGuardSlot = (slot) => {
  if (slot.type !== "action") return false;
  const binding = slot.binding;
  if (!binding) return false;
  if (binding.kind === "set_field" && binding.field === "currentSection") {
    return String(slot.id || "").indexOf("to-section") === 0;
  }
  if (binding.kind !== "process_trigger") return false;
  return binding.name !== PROCESS_TRIGGER_NAMES.BACK;
};

export const setBlocked = (element, blocked) => {
  if (blocked) {
    element.setAttribute("data-krs-state", "blocked");
    element.setAttribute("aria-disabled", "true");
    element.disabled = true;
    return;
  }
  if (element.getAttribute("data-krs-state") !== "blocked") return;
  element.removeAttribute("data-krs-state");
  element.removeAttribute("aria-disabled");
  element.disabled = false;
};

const slotSelector = (slot) =>
  slot.selector || `[data-kayros-slot='${slot.id}']`;

const elementFillsMissing = (element, item, doc) => {
  const inputEl = doc.querySelector(slotSelector(item.slot));
  return Boolean(inputEl && (inputEl === element || inputEl.contains(element)));
};

/**
 * A next trigger that sits inside a missing required input (choice cards
 * with data-action=next) is the control that fills that field. Blocking it
 * deadlocks the screen. Only block when another required field remains.
 */
export const shouldBlockTrigger = (element, slot, context, missing, doc) => {
  const conditions = slot.enabledWhen;
  const hasConditions = Boolean(conditions && conditions.length);
  const blockedByConditions =
    hasConditions && !evaluateConditions(conditions, context);
  if (!isRequiredGuardSlot(slot)) return blockedByConditions;
  const remaining = missing.filter(
    (item) => !elementFillsMissing(element, item, doc),
  );
  return blockedByConditions || remaining.length > 0;
};

export const applyBlockedState = (
  doc,
  slots,
  context,
  missing,
  options = {},
) => {
  if (!doc) return;
  const emptyDownloads = new Set(options.emptyDownloadSlotIds || []);
  for (const slot of slots) {
    const conditions = slot.enabledWhen;
    const hasConditions = Boolean(conditions && conditions.length);
    const guarded = isRequiredGuardSlot(slot);
    const isDownload =
      slot.type === "action" &&
      get(slot, "binding.kind") === "download_collection";
    const downloadEmpty = isDownload && emptyDownloads.has(slot.id);
    if (!hasConditions && !guarded && !isDownload) continue;
    const elements = doc.querySelectorAll(slotSelector(slot));
    if (!elements.length) continue;
    for (let i = 0; i < elements.length; i++) {
      setBlocked(
        elements[i],
        shouldBlockTrigger(elements[i], slot, context, missing, doc) ||
          downloadEmpty,
      );
    }
  }
};
